import {Icon} from './Icon';

export const LIFECYCLE_STEP_STATES = ['complete', 'current', 'blocked', 'upcoming'] as const;

export type LifecycleStepState = (typeof LIFECYCLE_STEP_STATES)[number];

export type LifecycleStep = Readonly<{
  description?: string | undefined;
  id: string;
  label: string;
  selectable?: boolean | undefined;
  state: LifecycleStepState;
}>;

type LifecycleProgressBaseProps = Readonly<{
  ariaLabel: string;
}>;

type AvailableLifecycleProgressProps = LifecycleProgressBaseProps & Readonly<{
  onSelect?: ((stepId: string) => void) | undefined;
  selectedStepId?: string | undefined;
  steps: ReadonlyArray<LifecycleStep>;
  unavailableReason?: never;
}>;

type UnavailableLifecycleProgressProps = LifecycleProgressBaseProps & Readonly<{
  onSelect?: never;
  selectedStepId?: never;
  steps?: never;
  unavailableReason: string;
}>;

export type LifecycleProgressProps = AvailableLifecycleProgressProps | UnavailableLifecycleProgressProps;

const stateLabels: Record<LifecycleStepState, string> = {
  blocked: 'Current step, blocked',
  complete: 'Complete',
  current: 'Current step',
  upcoming: 'Upcoming',
};

function lifecycleSymbol(state: LifecycleStepState, index: number) {
  if (state === 'complete') return <Icon name="circle-check"/>;
  if (state === 'blocked') return <Icon name="circle-alert"/>;
  return String(index + 1);
}

export function lifecycleCurrentStepCount(steps: ReadonlyArray<LifecycleStep>) {
  return steps.filter((step) => step.state === 'current' || step.state === 'blocked').length;
}

function LifecycleUnavailable({ ariaLabel, reason }: Readonly<{ ariaLabel: string; reason: string }>) {
  return <section
    aria-label={ariaLabel}
    className="lifecycle-progress lifecycle-progress--unavailable"
    data-state="unavailable"
  >
    <strong>Lifecycle position unavailable</strong>
    <p>{reason}</p>
  </section>;
}

export function LifecycleProgress(props: LifecycleProgressProps) {
  if ('unavailableReason' in props) {
    return <LifecycleUnavailable ariaLabel={props.ariaLabel} reason={props.unavailableReason} />;
  }

  const { ariaLabel, onSelect, selectedStepId, steps } = props;
  if (steps.length === 0 || lifecycleCurrentStepCount(steps) !== 1) {
    return <LifecycleUnavailable
      ariaLabel={ariaLabel}
      reason="The supplied lifecycle does not identify exactly one current or blocked step, so no position has been guessed."
    />;
  }

  return <nav className="lifecycle-progress" aria-label={ariaLabel}>
    <div
      aria-label={`${ariaLabel} all steps`}
      className="lifecycle-progress__viewport"
      role="region"
    >
      <ol className="lifecycle-progress__list">
        {steps.map((step, index) => {
          const isActive = step.state === 'current' || step.state === 'blocked';
          const isSelected = step.id === selectedStepId;
          const content = <>
            <span className="lifecycle-progress__marker" aria-hidden="true">{lifecycleSymbol(step.state, index)}</span>
            <span className="lifecycle-progress__copy">
              <strong>{step.label}</strong>
              <small>{stateLabels[step.state]}{isSelected && !isActive ? ' · Viewing' : ''}</small>
              {step.description && <span>{step.description}</span>}
            </span>
          </>;

          return <li
            className={`lifecycle-progress__step lifecycle-progress__step--${step.state}${isSelected ? ' lifecycle-progress__step--selected' : ''}`}
            data-state={step.state}
            key={step.id}
          >
            {onSelect && step.selectable
              ? <button
                aria-current={isActive ? 'step' : undefined}
                aria-pressed={isSelected}
                className="lifecycle-progress__control"
                onClick={() => onSelect(step.id)}
                type="button"
              >{content}</button>
              : <div aria-current={isActive ? 'step' : undefined} className="lifecycle-progress__control">{content}</div>}
          </li>;
        })}
      </ol>
    </div>
  </nav>;
}
